import {FeatureFlag} from '../../../@type/features';
import {onCheckoutSubmitErrorManual, onCheckoutSubmitManual} from '../checkout';
import {Feature} from '../features';
import {AuthnetButtonElement} from './class-authnet-element';
import {$qsAll} from '../../../@shared/ts/dom';
import {insertCaptchaFields, cleanupCaptchaFields} from '../botProtection';
import {cleanupTransactionFields, createPaymentTransaction, updatePaymentTransaction, insertTransactionFields} from '../transaction';

async function initAuthnetPayments($checkoutForm: HTMLFormElement): Promise<void> {
	if (!Feature.enabled(FeatureFlag.AUTHNET_GATEWAY)) {
		return;
	}

	const loginId = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'login_id');
	const clientKey = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'public_client_key');
	const acceptSrc = Feature.metadata<string>(FeatureFlag.AUTHNET_GATEWAY, 'accept_src');

	if (!clientKey || !loginId || !acceptSrc) {
		return;
	}

	const element = new AuthnetButtonElement({
		loginId,
		clientKey,
		acceptSrc,
	});

	onCheckoutSubmitManual(
		$checkoutForm,
		[
			'peachpay_authnet_card',
			'peachpay_authnet_echeck',
		],
		async gatewayId => {
			cleanupTransactionFields();
			cleanupTokenFields();
			cleanupCaptchaFields();

			const {token, error, cancel} = await element.tokenize(gatewayId === 'peachpay_authnet_card' ? 'card' : 'bank');

			const transactionId = await createPaymentTransaction(gatewayId);
			if (!transactionId) {
				return false;
			}

			if (cancel) {
				await updatePaymentTransaction(transactionId, {
					paymentStatus: 'cancelled',
					orderStatus: 'unperformed',
				});
				return false;
			}

			if (error ?? !token) {
				await updatePaymentTransaction(transactionId, {
					paymentStatus: 'creationfailed',
					orderStatus: 'unperformed',
					note: error?.map(error => `${error.code}: ${error.message}`).join('\n') ?? 'An unknown error occured while tokenizing the payment method.',
				});

				const target = '.payment_box:not([style*="display:none"]):not([style*="display: none"])';

				$qsAll(`${target} .peachpay-tokenize-error`, $el => {
					$el.classList.remove('hide');
					$el.innerHTML = error?.map(error => `${error.code}: ${error.message}`).join('\n') ?? 'An unknown error occured while tokenizing the payment method.';
				});

				setTimeout(() => {
					$qsAll('.peachpay-tokenize-error', $el => {
						$el.classList.add('hide');
						$el.innerHTML = '';
					});
				}, 10000);

				return false;
			}

			insertTransactionFields($checkoutForm, transactionId);
			insertTokenFields($checkoutForm, token.descriptor, token.value);
			await insertCaptchaFields($checkoutForm);

			return true;
		});

	onCheckoutSubmitErrorManual($checkoutForm, [
		'peachpay_authnet_card',
		'peachpay_authnet_echeck',
	], async (transactionId, errors) => {
		cleanupTransactionFields();
		cleanupTokenFields();
		cleanupCaptchaFields();

		if (!transactionId) {
			return;
		}

		try {
			await updatePaymentTransaction(transactionId, {
				paymentStatus: 'unperformed',
				orderStatus: 'creationfailed',
				note: errors.join('\n'),
			});
		} catch {
			// Do no harm.
		}
	});
}

function insertTokenFields($checkoutForm: HTMLFormElement, dataDescriptor: string, dataValue: string) {
	const $dataDescriptor = document.createElement('input');
	$dataDescriptor.type = 'hidden';
	$dataDescriptor.name = 'peachpay_authnet_data_descriptor';
	$dataDescriptor.value = dataDescriptor;
	$checkoutForm.insertAdjacentElement('beforeend', $dataDescriptor);

	const $dataValue = document.createElement('input');
	$dataValue.type = 'hidden';
	$dataValue.name = 'peachpay_authnet_data_value';
	$dataValue.value = dataValue;
	$checkoutForm.insertAdjacentElement('beforeend', $dataValue);
}

function cleanupTokenFields() {
	$qsAll('input[name="peachpay_authnet_data_descriptor"]', $el => {
		$el.remove();
	});

	$qsAll('input[name="peachpay_authnet_data_value"]', $el => {
		$el.remove();
	});
}

export {
	initAuthnetPayments,
};
